import * as JsSearch from 'js-search'
import createDebug from 'lib/util/createDebug'
import { formatDay, formatForSearchIndex, formatTime } from 'pages/Home/UpcomingWebinarsSection/util'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { HomeApiResponse } from '../Home'
import ProductCategory from '../ProductCategory'
import { filterRecordedWebinarsForSearch, filterUpcomingWebinarsForSearch, filterVideos } from '../sections/filtering'

const debug = createDebug('useSearchIndex')

// Same as https://github.com/bvaughn/js-search/blob/79dd9a9781c85233c4584cc81043295c30364cef/source/Tokenizer/SimpleTokenizer.js
// except doesn't remove periods
/* eslint-disable no-useless-escape */
const TOKENIZER_REGEX = /[^a-zа-яё0-9\-\.']+/i
class TrainingTokenizer implements JsSearch.ITokenizer {
  public tokenize(text: string): string[] {
    return text.split(TOKENIZER_REGEX).filter(
      text => text // Filter empty tokens
    )
  }
}

export default function useSearchIndex(
  currentProductCategory: ProductCategory,
  showAdminControls?: boolean,
  hidePastWebinars?: boolean,
  data?: HomeApiResponse
) {
  const [videosSearch, setVideosSearch] = useState(null)
  const [recordedWebinarsSearch, setRecordedWebinarsSearch] = useState(null)
  const [upcomingWebinarsSearch, setUpcomingWebinarsSearch] = useState(null)
  const [t] = useTranslation()

  useEffect(() => {
    if (data) {
      debug('Building search index')

      const videosIndex = new JsSearch.Search('id')
      videosIndex.indexStrategy = new JsSearch.AllSubstringsIndexStrategy()
      videosIndex.tokenizer = new TrainingTokenizer()
      videosIndex.addIndex('name')

      videosIndex.addDocuments(filterVideos(data.videos, currentProductCategory, showAdminControls))

      setVideosSearch(videosIndex)

      const recordedWebinarsIndex = new JsSearch.Search('id')
      recordedWebinarsIndex.indexStrategy = new JsSearch.AllSubstringsIndexStrategy()
      recordedWebinarsIndex.tokenizer = new TrainingTokenizer()
      recordedWebinarsIndex.addIndex('title')
      recordedWebinarsIndex.addIndex('description')
      recordedWebinarsIndex.addIndex('skillLevel')
      recordedWebinarsIndex.addIndex(['series', 'title'])

      recordedWebinarsIndex.addDocuments(
        filterRecordedWebinarsForSearch(data.recordedWebinars, currentProductCategory, showAdminControls)
      )

      setRecordedWebinarsSearch(recordedWebinarsIndex)

      const upcomingWebinarsIndex = new JsSearch.Search('id')
      upcomingWebinarsIndex.indexStrategy = new JsSearch.AllSubstringsIndexStrategy()
      upcomingWebinarsIndex.tokenizer = new TrainingTokenizer()
      upcomingWebinarsIndex.addIndex('title')
      upcomingWebinarsIndex.addIndex('description')
      upcomingWebinarsIndex.addIndex('presenter')
      upcomingWebinarsIndex.addIndex('day')
      upcomingWebinarsIndex.addIndex('time')
      upcomingWebinarsIndex.addIndex('dateLabel')
      upcomingWebinarsIndex.addIndex(['recordedWebinar', 'skillLevel'])
      upcomingWebinarsIndex.addIndex(['recordedWebinar', 'series', 'title'])

      const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone

      upcomingWebinarsIndex.addDocuments(
        filterUpcomingWebinarsForSearch(
          data.upcomingWebinars,
          currentProductCategory,
          showAdminControls,
          hidePastWebinars
        ).map(upcomingWebinar => ({
          ...upcomingWebinar,
          day: formatDay(localTimeZone, upcomingWebinar.date, t),
          time: formatTime(localTimeZone, upcomingWebinar.date, t),
          dateLabel: formatForSearchIndex(localTimeZone, upcomingWebinar.date, t)
        }))
      )

      setUpcomingWebinarsSearch(upcomingWebinarsIndex)
    }
  }, [currentProductCategory, data, t, showAdminControls, hidePastWebinars])

  return { videosSearch, recordedWebinarsSearch, upcomingWebinarsSearch }
}
